import { useElementSize } from '@mantine/hooks';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Badge,
  CircularProgress,
  Fade,
  IconButton,
  Stack,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  useTheme,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { useSearch } from 'components/Search/SearchContextProvider';
import {
  getBadgeIconSx,
  getDefaultIconSx,
  getIconButtonSx,
  getSearchTextfieldSx,
  getTextInputBorderContainerSx,
} from 'components/Search/styles';
import {
  Dispatch,
  Fragment,
  memo,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useListContext, useResourceContext } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { omit } from 'remeda';
import { bloxValues } from 'themes/constants';
import {
  qpUpdateHelper,
  useStateWithDebouncedQueryParams,
} from 'utility/hooks/useStateWithDebouncedCallback';
import { getPrimaryBgColor } from 'utility/styles';
import { getPlaceholderText } from '../../functions';
import { SearchAdvancedPopover } from './SearchAdvancedPopover';
import { useAdvancedSearchContext } from './useAdvancedSearchContext';
import { useDebouncedCallback } from '@mantine/hooks';

/**
 * SearchTextField + useSearchTextField
 *
 * The single mounted search text input. It replaces the old pair
 * of conditionally rendered inputs (standard + AISearchInput).
 *
 * Reasoning: Mounting/unmounting a component that owns a url-synced hook was
 * was corrupting the swap of query <-> semantic onActivate/deactivate handling.
 * This approach keeps ONE text input mounted at all times and just
 * changes the value/handler/adornments based on the current mode.
 *
 * It sets up one hook per param (query, raw_query, semantic). They are
 * all mounted at once; only the active mode's hook is enabled to write, and
 * the displayed value + change handler waterfall down to the correct one.
 *
 * Debugging Info:
 * In detail, with the mount/unmounting of two components the handleAiSearchActivate
 * handler would run and the URL would end up reverting back to the prior state eg:
 * - User clicks AI Search toggle .../editorial/search?query=test
 * - Briefly URL was changed to .../editorial/search?semantic=test
 * - Then unmount reverted to .../editorial/search?query=test
 *
 * If you are editing this file with AI, and it tries to tell you
 * that useStateWithDebouncedQueryParams has a problem or race condition
 * the AI is wrong. The actual problem was is the mount/unmount approach.
 */

type TUseSearchTextFieldArgs = {
  aiSearchEnabled: boolean;
  useRawQuery: boolean;
  showSubmitBtn: boolean;
  textInputRef: RefObject<HTMLInputElement>;
};

type TUseSearchTextFieldResult = {
  rawInput: string;
  setRawInput: Dispatch<SetStateAction<string>>;
  setTextValue: (next: string) => void;
  getInitialTextInput: () => string;
  paramTarget: string;
  queryValue: string;
  setQueryValue: (next: string) => void;
  rawQueryValue: string;
  setRawQueryValue: (next: string) => void;
  semanticValue: string;
  setSemanticValue: (next: string) => void;
  textFieldValue: string;
};

export const useSearchTextField = ({
  aiSearchEnabled,
  useRawQuery,
  showSubmitBtn,
  textInputRef,
}: TUseSearchTextFieldArgs): TUseSearchTextFieldResult => {
  const { validListFilter, isModalSearch } = useSearch();

  // Initialize values via VLF (source of truth).
  // Should be correct for both modal/qp modes.
  const getInitialTextInput = useCallback((): string => {
    if (aiSearchEnabled) {
      return validListFilter?.semantic ?? '';
    }
    if (useRawQuery) {
      return validListFilter?.raw_query ?? '';
    }
    return validListFilter?.query ?? '';
  }, [aiSearchEnabled, useRawQuery, validListFilter]);

  // Which key for read-write? (query, raw_query, semantic)
  const paramTarget: string = useMemo(() => {
    if (aiSearchEnabled) {
      return 'semantic';
    }
    if (useRawQuery) {
      return 'raw_query';
    }
    return 'query';
  }, [aiSearchEnabled, useRawQuery]);

  // Non-debounced local text input state...
  const [rawInput, setRawInput] = useState<string>(getInitialTextInput);

  // Submit and modal keeps text in rawInput local state
  // so all url-synced hooks are disabled in those modes.
  const urlSyncDisabled = showSubmitBtn || isModalSearch;

  // One hook per param. All mounted; each syncs its own param.
  // Do not try and conditionally modify one hook for different params.
  // That will lead to problems, and cleaner to have a dedicated one for each.
  const [queryValue, setQueryValue] = useStateWithDebouncedQueryParams({
    qpkey: 'query',
    initialValue: validListFilter?.query ?? '',
    disabled: urlSyncDisabled || useRawQuery,
  });

  const [rawQueryValue, setRawQueryValue] = useStateWithDebouncedQueryParams({
    qpkey: 'raw_query',
    initialValue: validListFilter?.raw_query ?? '',
    disabled: urlSyncDisabled || !useRawQuery,
  });

  const [semanticValue, setSemanticValue] = useStateWithDebouncedQueryParams({
    qpkey: 'semantic',
    initialValue: validListFilter?.semantic ?? '',
    disabled: urlSyncDisabled,
  });

  // Which value is shown in the field.
  const textFieldValue = useMemo(() => {
    if (showSubmitBtn || isModalSearch) {
      return rawInput;
    }

    if (aiSearchEnabled) {
      return semanticValue;
    }
    if (useRawQuery) {
      return rawQueryValue;
    }
    return queryValue;
  }, [
    showSubmitBtn,
    isModalSearch,
    aiSearchEnabled,
    useRawQuery,
    rawInput,
    semanticValue,
    rawQueryValue,
    queryValue,
  ]);

  /**
   * Targets setter for updating raw input state.
   *
   * Note: Can handle the filter updates in onChanges
   * or elsewhere, this is mainly concerned with just
   * the local rawInput useState...
   */
  const setTextValue = useCallback(
    (value: string) => {
      if (showSubmitBtn || isModalSearch) {
        setRawInput(value);
        return;
      }

      // Standard modes...
      if (aiSearchEnabled) {
        setSemanticValue(value);
        return;
      }
      if (useRawQuery) {
        setRawQueryValue(value);
        return;
      }
      setQueryValue(value);
    },
    [
      aiSearchEnabled,
      isModalSearch,
      setQueryValue,
      setRawQueryValue,
      setSemanticValue,
      showSubmitBtn,
      useRawQuery,
    ],
  );

  const hookValue = useMemo(() => {
    return {
      rawInput,
      setRawInput,
      setTextValue,
      getInitialTextInput,
      paramTarget,
      queryValue,
      setQueryValue,
      rawQueryValue,
      setRawQueryValue,
      semanticValue,
      setSemanticValue,
      textFieldValue,
    };
  }, [
    getInitialTextInput,
    paramTarget,
    queryValue,
    rawInput,
    rawQueryValue,
    semanticValue,
    setQueryValue,
    setRawQueryValue,
    setSemanticValue,
    textFieldValue,
    setTextValue,
  ]);

  return hookValue;
};

/**
 * Textfield component with handlers and effects...
 */

type SearchTextFieldProps = {
  rawInput: TUseSearchTextFieldResult['rawInput'];
  setRawInput: TUseSearchTextFieldResult['setRawInput'];
  setTextValue: TUseSearchTextFieldResult['setTextValue'];
  textFieldValue: TUseSearchTextFieldResult['textFieldValue'];
  paramTarget: TUseSearchTextFieldResult['paramTarget'];
  textInputRef: RefObject<HTMLInputElement>;
  searchDropdownComponent?: React.ReactNode;
  advancedPopoverFieldsComponent?: React.ReactNode;
  aiSearchEnabled: boolean;
  useRawQuery: boolean;
  effectiveDomains?: string[];
  onSubmitResetSearchState?: () => void;
  showSubmitBtn?: boolean;
  lastSubmittedSearchRef?: React.MutableRefObject<string | null>;
};

export const SearchTextField = memo(
  ({
    rawInput,
    setRawInput,
    textFieldValue,
    setTextValue,
    paramTarget,
    aiSearchEnabled,
    useRawQuery,
    showSubmitBtn,
    lastSubmittedSearchRef,
    effectiveDomains,
    textInputRef,
    searchDropdownComponent,
    advancedPopoverFieldsComponent,
    onSubmitResetSearchState,
  }: SearchTextFieldProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const resource = useResourceContext();
    const { isFetching, isLoading, refetch } = useListContext();
    const listRefetchRef = useRef<() => void>();
    const {
      queryParams,
      validListFilter,
      preservedKeysArray,
      updateQueryParams,
      isModalSearch,
      updateFilter,
      resourceContexts,
    } = useSearch();
    const { advancedFiltersCount, setStagedFilters, isNormal } =
      useAdvancedSearchContext();

    const wireSearchCtx = useMemo(() => {
      return resourceContexts?.['wire/channel/search'] ?? null;
    }, [resourceContexts]);

    const fetchingOrLoading = isFetching || isLoading;

    const hasText = Boolean(textFieldValue.trim());

    /**
     * Specific to modal mode, debounced update handler.
     * This is to avoid double state updates and races
     * with useEffect update cycles, instead directly call
     * a debounced update handler when changes occur.
     */

    // Modal mode and update function provided
    const isModalUpdate = typeof updateFilter === 'function' && Boolean(isModalSearch);

    // The useDebouncedCallback needs a function passed,
    // this wrapper is just to ensure the function exists.
    const updateModalFilter = useCallback(
      (key: string, value: any) => {
        if (!isModalUpdate) {
          return;
        }
        updateFilter(key, value);
      },
      [isModalUpdate, updateFilter],
    );

    // Debounced update for modal filter changes
    const dbUpdateModalFilter = useDebouncedCallback(updateModalFilter, { delay: 400 });

    /**
     * Handle input events and clear button clicks.
     */
    const handleTextChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const value = event.target.value;
      setTextValue(value);
      if (isModalUpdate) {
        dbUpdateModalFilter(paramTarget, value);
      }
    };

    // Handle clear button click
    const handleClearClick = () => {
      setTextValue('');
      if (isModalUpdate) {
        dbUpdateModalFilter(paramTarget, '');
      }
      textInputRef?.current?.focus();
    };

    /**
     * Multisite user search, modal handlers, etc...
     *
     * All this feels edge-case and needs to be
     * refactored or cleaned up. Was ported over
     * as-is from SearchAdvancedTopBar.tsx prior code.
     * There are too many effects here trying to sync or
     * guess at initial values, refetching triggers, etc.
     */
    const sort = queryParams.get('sort') || '';
    const dir = queryParams.get('dir') || '';
    const domainKey = effectiveDomains?.sort().join(',') || '';
    const prevDomainsKeyRef = useRef<string>(domainKey);
    const currentSearchKey = `${rawInput.trim()}::${domainKey}`;
    const isDuplicateSubmission = lastSubmittedSearchRef?.current === currentSearchKey;
    const submitBtnDisabled = isDuplicateSubmission || rawInput.trim() === '';

    const hadQueryOnInitialLoad = useRef(
      useRawQuery
        ? queryParams.get('raw_query')?.trim() || false
        : queryParams.get('query')?.trim() || false,
    );

    /**
     * This is just for the effect and ref below.
     * Active filter comparison value from validListFilter (VLF).
     * VLF is the validated/normalized source of truth this component uses.
     * Note: In modal mode `queryParams` is a simulation ModalSearchCtx provides.
     */
    const activeValue: string = useMemo(() => {
      const value = validListFilter?.[paramTarget];
      return typeof value === 'string' ? value : '';
    }, [validListFilter, paramTarget]);

    const prevActiveValueRef = useRef<string>(activeValue);

    /**
     * Modal: sync rawInput with external filter changes.
     *
     * Track prev to detect external changes (eg: quick filter updates).
     * In modal mode the search filter lives in local state provider
     * state, not in URL params. When a quick filter is applied it may set
     * or clear the text - sync that back to rawInput so the text field
     * follows (clears if removed, populates if set).
     *
     * Reading `paramTarget` keeps the value stable across
     * programmatic changes (eg: query/semantic) so we dont
     * momentarily read a cleared value and blank the input.
     */
    useEffect(() => {
      if (!isModalSearch) {
        return;
      }
      if (activeValue !== prevActiveValueRef.current) {
        setRawInput(activeValue);
      }
      prevActiveValueRef.current = activeValue;
    }, [isModalSearch, activeValue, setRawInput]);

    /**
     * Multisite user search - manual submit mode.
     */
    const handleSubmitClick = () => {
      const trimmedQuery = rawInput.trim();
      if (!trimmedQuery || isDuplicateSubmission) {
        return;
      }

      onSubmitResetSearchState?.();

      // Set search param (URL) to trigger search
      qpUpdateHelper(
        useRawQuery ? 'raw_query' : 'query',
        trimmedQuery,
        updateQueryParams,
      );

      // If the query param desired was in place on initial load,
      // force a "refetch" to ensure the search is allowed to process
      // and not seen as a repeat query.
      if (lastSubmittedSearchRef?.current === 'ready' && hadQueryOnInitialLoad.current) {
        refetch?.();
      }

      // Update the last submitted search reference
      if (lastSubmittedSearchRef) {
        lastSubmittedSearchRef.current = currentSearchKey;
      }
      prevDomainsKeyRef.current = domainKey;
    };

    // Require Enter key for submit button mode
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSubmitBtn) {
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSubmitClick();
      }
    };

    // Sync list context refetch function
    useEffect(() => {
      listRefetchRef.current = refetch;
    }, [refetch]);

    // Allow sort/dir changes to trigger a refetch for submit button searches
    useEffect(() => {
      if (!showSubmitBtn) {
        return;
      }

      const trimmedQuery = textInputRef.current?.value?.trim();
      if (!trimmedQuery || !sort || !dir) {
        return;
      }

      onSubmitResetSearchState?.();

      // Defer refetch slightly
      const timeout = setTimeout(() => {
        listRefetchRef.current?.();
      }, 50);

      return () => clearTimeout(timeout);
    }, [dir, onSubmitResetSearchState, showSubmitBtn, sort, textInputRef]);

    // Allow domain changes to trigger a refetch for submit button searches
    useEffect(() => {
      if (
        !showSubmitBtn ||
        !lastSubmittedSearchRef ||
        effectiveDomains?.includes('ALL_SITES')
      ) {
        return;
      }

      const trimmedQuery = textInputRef.current?.value?.trim();
      if (!trimmedQuery) {
        return;
      }

      qpUpdateHelper(
        useRawQuery ? 'raw_query' : 'query',
        trimmedQuery,
        updateQueryParams,
      );
      prevDomainsKeyRef.current = domainKey;
      onSubmitResetSearchState?.();

      const isInitialized =
        Array.isArray(effectiveDomains) &&
        effectiveDomains.length > 0 &&
        !(effectiveDomains.length === 1 && effectiveDomains[0] === 'ALL_SITES');

      if (isInitialized && lastSubmittedSearchRef.current !== null) {
        const timeout = setTimeout(() => {
          listRefetchRef?.current?.();
          if (domainKey) {
            lastSubmittedSearchRef.current = `${trimmedQuery}::${domainKey}`;
          }
        }, 50);

        return () => clearTimeout(timeout);
      }

      // Set ready when domains settled
      if (domainKey) {
        lastSubmittedSearchRef.current = 'ready';
      }
    }, [
      domainKey,
      effectiveDomains,
      lastSubmittedSearchRef,
      onSubmitResetSearchState,
      showSubmitBtn,
      textInputRef,
      updateQueryParams,
      useRawQuery,
    ]);

    // Get placeholder text and sort options using helper functions
    const searchPlaceholderText = getPlaceholderText({
      resource,
      resourceContexts,
      isNormal,
      t,
    });

    const searchTextEnabled = useMemo<boolean>(() => {
      switch (resource) {
        case 'wire/channel/search': {
          // enabled if not in wire search context
          if (!wireSearchCtx) {
            return true;
          }
          // else check the channel/option for 'query' and type 'string'
          return (
            wireSearchCtx?.channelOptions?.some(
              (option) =>
                (option.name === 'query' || option.name === 'raw_query') &&
                option.type === 'string',
            ) ?? false
          );
        }
        case 'wire/channel': {
          return false; // No text search for channels list, only syndication feeds
        }
        default: {
          return true;
        }
      }
    }, [wireSearchCtx, resource]);

    const placeholderText = useMemo(() => {
      if (aiSearchEnabled) {
        return t('blox.ai.search.placeholder');
      }
      if (!searchTextEnabled) {
        return t(
          'blox.general.search_text_disabled_placeholder',
          'Text search disabled...',
        );
      }
      return searchPlaceholderText;
    }, [aiSearchEnabled, searchTextEnabled, searchPlaceholderText, t]);

    /**
     * Popover filter and handlers for staging adv filters
     */
    const { ref: elSizeRef, width: searchBoxWidth } = useElementSize();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const popoverOpen = Boolean(anchorEl);

    // Update staged filters to match validListFilter, minus preserved keys
    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(elSizeRef.current);
      setStagedFilters(() => omit(validListFilter, preservedKeysArray));
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    /**
     * Memoized styles
     */
    const stackSx: SxProps<Theme> = useMemo(() => {
      const inputBorder = getTextInputBorderContainerSx(theme);

      const aiSearchSx: SystemStyleObject = aiSearchEnabled
        ? {
            height: bloxValues.iconSizeLarge + 2,
            borderColor: theme.palette.primary.main,
          }
        : {};

      return {
        ...inputBorder,
        alignItems: 'center',
        flexGrow: 1,
        minWidth: 0,
        ...aiSearchSx,
      };
    }, [aiSearchEnabled, theme]);

    const textInputSx: SxProps<Theme> = useMemo(() => {
      const styles = getSearchTextfieldSx(theme, !searchTextEnabled);
      return styles;
    }, [searchTextEnabled, theme]);

    const advFiltersBtnSx: SxProps<Theme> = useMemo(() => {
      const iconBtnSx = getIconButtonSx(theme);
      const primaryBg = getPrimaryBgColor(theme);
      const primaryBgHover = getPrimaryBgColor(theme, true);
      return {
        ...iconBtnSx,
        bgcolor: advancedFiltersCount !== 0 ? primaryBg : undefined,
        '&:hover': {
          bgcolor: advancedFiltersCount !== 0 ? primaryBgHover : undefined,
        },
      };
    }, [advancedFiltersCount, theme]);

    return (
      <Fragment>
        <Stack
          id="searchinputs-textinput-border-container"
          ref={elSizeRef}
          direction="row"
          sx={stackSx}
        >
          {/* AI icon in semantic mode */}
          {aiSearchEnabled && (
            <AutoAwesomeOutlinedIcon
              fontSize="small"
              sx={{
                color: theme.palette.primary.main,
                ml: 0.5,
                mr: 0.5,
                fontSize: bloxValues.iconSizeRegular,
              }}
            />
          )}

          {/* Search Dropdown Component */}
          {searchDropdownComponent}

          <TextField
            data-testid="topbar-search-text-input"
            inputRef={textInputRef}
            size="small"
            autoComplete="off"
            value={textFieldValue}
            data-private={true}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholderText}
            variant="outlined"
            fullWidth={true}
            sx={textInputSx}
            disabled={!searchTextEnabled}
          />

          <Fade
            in={fetchingOrLoading}
            appear={true}
            unmountOnExit={true}
          >
            <CircularProgress
              size={14}
              thickness={3}
              color="primary"
              variant="indeterminate"
              title={t('blox.general.loading')}
            />
          </Fade>

          <Fade
            in={hasText}
            appear={true}
            unmountOnExit={true}
          >
            <Tooltip title={t('blox.actions.clear')}>
              <IconButton
                sx={getIconButtonSx}
                onClick={handleClearClick}
                data-testid={'topbar-clear-search-query-btn'}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Fade>

          {/* Multisite Search Submit Button (standard mode only) */}
          {showSubmitBtn && (
            <Tooltip title={t('blox.actions.search')}>
              <span>
                <IconButton
                  sx={getIconButtonSx}
                  color="primary"
                  onClick={handleSubmitClick}
                  disabled={submitBtnDisabled}
                  data-testid="topbar-search-submit-btn"
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Advanced filters button  */}
          {advancedPopoverFieldsComponent && (
            <Tooltip title={t('blox.navigation.assets.advanced_filters')}>
              <IconButton
                data-testid="search-advanced-filters-button"
                onClick={handlePopoverOpen}
                sx={advFiltersBtnSx}
              >
                <Badge
                  badgeContent={advancedFiltersCount}
                  aria-label={`${advancedFiltersCount} advanced filter categories are applied.`}
                  showZero={advancedFiltersCount !== 0}
                  color="primary"
                  sx={getBadgeIconSx}
                >
                  <TuneIcon sx={getDefaultIconSx} />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* Popover is a portal — DOM position does not affect placement */}
        {advancedPopoverFieldsComponent && (
          <SearchAdvancedPopover
            open={popoverOpen}
            anchorEl={anchorEl}
            onCloseCallback={handlePopoverClose}
            popoverSx={{ width: searchBoxWidth + 5 }}
            advancedPopoverFieldsComponent={advancedPopoverFieldsComponent}
          />
        )}
      </Fragment>
    );
  },
);
SearchTextField.displayName = 'SearchTextField';
